import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import ApiClient from '../../methods/api/apiClient';
import loader from '../../methods/loader';
import environment from '../../environment';
import { decryptId } from '../../components/common/Encryption/encryption';
import RegistrationCard from './RegistrationCard';

// cw: Registration List — the "Who's coming" expanded photolist of everyone who RSVP'd
// Yes to one event. Served at /registration-list/:eventid (encrypted id), shareable and
// renderable logged-out (read-only). Logged-in Yes attendees get click-to-request
// {connectMeetLabel}/{testimonialLabel} on each card, feeding the same endpoints the
// EventDetail flows use (api/group/request, api/testimonial/submit).
const RegistrationList: React.FC = () => {
    const { eventid: eventidParam } = useParams();
    const navigate = useNavigate();
    const user = useSelector((state: any) => state.user);

    const [eventId, setEventId] = useState<string | null>(null);
    const [event, setEvent] = useState<any>(null);
    const [attendees, setAttendees] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState<string | null>(null);

    const groupId = event?.groupId?._id || event?.groupId;
    const connectMeetLabel = event?.groupId?.connectMeetLabel || user?.connectMeetLabel || 'ConnectMeet';
    const testimonialLabel = user?.testimonialLabel || 'Thank You';

    const loggedIn = !!user?.loggedIn;
    const groupLeaderRoleIds = environment.groupLeaderRoleIds || [];
    const isGl = groupLeaderRoleIds.includes(user?.customerRole?._id || user?.customerRole);

    // My own attendee record for this event (need attendeeId + Yes/checked-in to act).
    const myAttendee = useMemo(() => {
        if (!loggedIn) return null;
        return attendees.find((a: any) =>
            (a?.memberDetails?.email || a?.email) === user?.email ||
            (a?.userId && user?._id && a.userId === user._id)
        );
    }, [attendees, user, loggedIn]);

    const iAmYes = !!myAttendee && (myAttendee.meetConfirm === 'Yes' || myAttendee.isPresent === true);

    useEffect(() => {
        const resolve = async () => {
            if (!eventidParam) return;
            const decoded = /^[0-9a-fA-F]{24}$/.test(eventidParam)
                ? eventidParam
                : await decryptId(eventidParam);
            setEventId(decoded);
        };
        resolve();
    }, [eventidParam]);

    const loadData = (id: string) => {
        setLoading(true);
        loader(true);
        Promise.all([
            ApiClient.get('api/event/details', { id, email: user?.email }),
            ApiClient.get('api/attendees/list', { eventId: id, meetConfirm: 'Yes' }),
        ]).then(([evRes, attRes]: any) => {
            if (evRes?.success) setEvent(evRes.data);
            if (attRes?.success) setAttendees(attRes.data || []);
            setLoading(false);
            loader(false);
        }).catch(() => {
            setLoading(false);
            loader(false);
        });
    };

    useEffect(() => {
        if (eventId) loadData(eventId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventId]);

    // CM request for THIS event: append target to my connect_with_members group request
    // (mirrors ManagerRequestForm.handleSubmit — read existing, append, do not overwrite).
    const requestCm = async (member: any) => {
        if (!loggedIn) { toast.info('Please sign in to make a request'); return; }
        if (!iAmYes) { toast.error('You must have RSVP\'d Yes to this event to request'); return; }
        const targetAttendeeId = member.id || member._id;
        if (!targetAttendeeId || !eventId || !groupId) return;

        setBusyId(targetAttendeeId);
        try {
            const existingRes: any = await ApiClient.get('api/group-request/list', {
                eventId, requestedBy: user?.email,
            });
            let ids: string[] = [];
            if (existingRes?.success && existingRes?.data?.length > 0) {
                const existing = existingRes.data[0];
                ids = (existing.attendeesDetails || [])
                    .filter((a: any) => a?._id).map((a: any) => a._id.toString());
            }
            if (!ids.includes(targetAttendeeId.toString())) ids.push(targetAttendeeId.toString());

            const payload: any = {
                eventId,
                groupId,
                requestedBy: user?.email,
                attendeeIds: ids,
                requestType: 'connect_with_members',
                attendeeId: myAttendee?.id || myAttendee?._id,
            };
            if (myAttendee?.userId || user?._id) payload.userId = myAttendee?.userId || user?._id;

            const res: any = await ApiClient.post('api/group/request', payload);
            if (res?.success) {
                toast.success(`${connectMeetLabel} request sent`);
            } else {
                toast.error(res?.message || `Failed to send ${connectMeetLabel} request`);
            }
        } catch (e) {
            toast.error(`Failed to send ${connectMeetLabel} request`);
        } finally {
            setBusyId(null);
        }
    };

    // TY request for THIS event: submit to the testimonial queue.
    const requestTy = async (member: any) => {
        if (!loggedIn) { toast.info('Please sign in to make a request'); return; }
        if (!iAmYes) { toast.error('You must have RSVP\'d Yes to this event to give a ' + testimonialLabel); return; }
        const receivedById = member?.memberDetails?._id || member?.userId;
        if (!receivedById) { toast.error('This person has no member profile to receive a ' + testimonialLabel); return; }
        if (receivedById === user?._id) { toast.error(`You can't ${testimonialLabel.toLowerCase()} yourself`); return; }

        setBusyId(member.id || member._id);
        try {
            const res: any = await ApiClient.post('api/testimonial/submit', {
                receivedById,
                groupId,
                eventId,
            });
            if (res?.success) {
                toast.success(`${testimonialLabel} request submitted`);
            } else {
                toast.error(res?.message || `Failed to submit ${testimonialLabel}`);
            }
        } catch (e) {
            toast.error(`Failed to submit ${testimonialLabel}`);
        } finally {
            setBusyId(null);
        }
    };

    const copyShareLink = () => {
        const url = window.location.href;
        if (navigator.clipboard) {
            navigator.clipboard.writeText(url).then(() => toast.success('Share link copied'));
        } else {
            toast.info(url);
        }
    };

    return (
        <div className="min-h-screen bg-[#f7f7f8]">
            <div className="max-w-4xl mx-auto px-4 py-6">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                    <div>
                        <h3 className="text-xl font-semibold text-gray-900">Who's Coming</h3>
                        {event?.title && <p className="text-sm text-gray-500">{event.title}</p>}
                        <p className="text-xs text-gray-400 mt-0.5">
                            {attendees.length} registered {attendees.length === 1 ? 'attendee' : 'attendees'}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {loggedIn && (
                            <button
                                onClick={() => navigate(-1)}
                                className="text-sm px-3 h-9 rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
                            >
                                Back
                            </button>
                        )}
                        {(isGl || loggedIn) && (
                            <button
                                onClick={copyShareLink}
                                className="text-sm px-3 h-9 rounded-lg bg-[#EF7A2B] text-white hover:bg-orange-600"
                            >
                                Copy share link
                            </button>
                        )}
                    </div>
                </div>

                {loggedIn && !iAmYes && (
                    <div className="mb-4 text-xs text-gray-500 bg-white border border-gray-200 rounded-lg px-3 py-2">
                        You are viewing the registration list. RSVP Yes to this event to request a
                        {' '}{connectMeetLabel} or give a {testimonialLabel}.
                    </div>
                )}

                {loading ? (
                    <div className="text-center py-16 text-gray-400">Loading…</div>
                ) : attendees.length === 0 ? (
                    <div className="text-center py-16 text-gray-400">No one has RSVP'd Yes yet.</div>
                ) : (
                    <div className="space-y-3">
                        {attendees.map((m: any) => (
                            <RegistrationCard
                                key={m.id || m._id}
                                member={m}
                                connectMeetLabel={connectMeetLabel}
                                testimonialLabel={testimonialLabel}
                                canAct={loggedIn && iAmYes && (m.id || m._id) !== (myAttendee?.id || myAttendee?._id)}
                                busy={busyId === (m.id || m._id)}
                                onRequestCm={requestCm}
                                onRequestTy={requestTy}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RegistrationList;
